/* Karte im Pia-Thread (30.10.2026): {art, titel, daten, knoepfe[{text, aktion}]} generisch gerendert. Die Karten der späteren
   Stufen (Briefing, Lecks-Report, Nachbereitung) füllen nur `daten`; hier gibt es keine feste Form pro Art.
   daten.zeilen = [{text, wert?, link?}] wird als Liste gezeigt, daten.abschnitte = [{titel, text}] als Abschnitte,
   alles andere als „Schlüssel: Wert". aktion: {typ: 'link', ziel} oder {typ: 'api', pfad, methode?, daten?} oder (Nachbereitung, Stufe 5)
   {typ: 'sprachnotiz', pfad}: Aufnahme im Browser (MediaRecorder) oder getippter Text, Antwort ist eine neue Vorschlagskarte im Thread.
   Karten mit daten.aktion_id (Schreib-Aktionen) laden ihren Stand nach: erledigte Schritte sind abgehakt, abgebrochene Karten ohne Knöpfe. */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

const text = (v) => (v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v));

function Zeile({ z }) {
  const inhalt = (
    <span className="flex items-start justify-between gap-3 py-1 text-sm">
      <span>{text(z.text)}</span>{z.wert != null && <span className="text-muted-foreground text-right shrink-0">{text(z.wert)}</span>}
    </span>
  );
  return z.link && String(z.link).startsWith('/')
    ? <Link to={z.link} className="block border-b border-border last:border-0 hover:bg-muted/50 rounded px-1">{inhalt}</Link>
    : <div className="border-b border-border last:border-0 px-1">{inhalt}</div>;
}


const MAX_SEKUNDEN = 180;
const VERSTECKT = ['aktion_id', 'aktion_art', 'termin_art', 'termin_id', 'sprachnotiz_pfad'];     // Verwaltungsfelder, nicht für die Anzeige

/* Sprachnotiz zur Nachbereitung: aufnehmen und beim Beenden senden, oder lieber tippen. Das Backend ändert nichts, es legt nur die Vorschlagskarte an. */
function SprachnotizBox({ pfad, onFertig }) {
  const [nimmtAuf, setNimmtAuf] = useState(false);
  const [sek, setSek] = useState(0);
  const [busy, setBusy] = useState(false);
  const [tippen, setTippen] = useState(false);
  const [text, setText] = useState('');
  const [gesendet, setGesendet] = useState(false);
  const recorder = useRef(null);
  const teile = useRef([]);
  const timer = useRef(null);
  const stopp = () => { try { if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop(); } catch { /* egal */ } };
  useEffect(() => () => { clearInterval(timer.current); stopp(); }, []);

  const senden = async (blob, getippt) => {
    setBusy(true);
    try {
      const fd = new FormData();
      if (blob) fd.append('audio', new File([blob], `nachbereitung.${blob.type.includes('mp4') ? 'm4a' : blob.type.includes('ogg') ? 'ogg' : 'webm'}`, { type: blob.type }));
      if (getippt) fd.append('text', getippt);
      await api.post(pfad, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 150000 });
      toast.success('Verstanden – die Vorschlagskarte steht im Chat');
      setGesendet(true); setText(''); setTippen(false);
      window.dispatchEvent(new Event('pia-auftraege-aktualisiert'));
      onFertig?.();
    } catch (e) { toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Nicht verarbeitet'); }
    setBusy(false);
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') { toast.error('Aufnahme in diesem Browser nicht möglich. Bitte tippen.'); setTippen(true); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const typ = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].find((t) => MediaRecorder.isTypeSupported?.(t)) || '';
      const rec = new MediaRecorder(stream, typ ? { mimeType: typ } : undefined);
      teile.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) teile.current.push(e.data); };
      rec.onstop = () => {
        clearInterval(timer.current); setNimmtAuf(false);
        stream.getTracks().forEach((t) => t.stop());
        const b = new Blob(teile.current, { type: (rec.mimeType || 'audio/webm').split(';')[0] });
        if (b.size < 1000) { toast.error('Aufnahme zu kurz'); return; }
        senden(b, '');
      };
      recorder.current = rec; rec.start(1000);
      setNimmtAuf(true); setSek(0);
      timer.current = setInterval(() => setSek((n) => { if (n + 1 >= MAX_SEKUNDEN) stopp(); return n + 1; }), 1000);
    } catch { toast.error('Mikrofon nicht freigegeben – du kannst auch tippen'); setTippen(true); }
  };

  return (
    <div className="space-y-2" data-testid="pia-sprachnotiz">
      <div className="flex flex-wrap gap-2">
        {nimmtAuf
          ? <Button size="sm" variant="destructive" onClick={stopp} data-testid="pia-aufnahme-stopp">Aufnahme beenden und senden ({sek}s)</Button>
          : <Button size="sm" disabled={busy} onClick={start} data-testid="pia-aufnahme-start">{busy ? 'Pia hört zu …' : gesendet ? 'Noch eine Sprachnotiz' : 'Sprachnotiz aufnehmen'}</Button>}
        {!nimmtAuf && <Button size="sm" variant="outline" disabled={busy} onClick={() => setTippen((t) => !t)}>Lieber tippen</Button>}
      </div>
      {tippen && (
        <div className="space-y-2">
          <textarea className="w-full rounded-md border border-border bg-background p-2 text-sm min-h-[80px]" value={text} maxLength={3000}
            placeholder="z. B. Hat unterschrieben, Premium. Meldet sich Donnerstag wegen der Probewoche." onChange={(e) => setText(e.target.value)} data-testid="pia-nachbereitung-text" />
          <Button size="sm" disabled={busy || text.trim().length < 3} onClick={() => senden(null, text.trim())}>Senden</Button>
        </div>
      )}
    </div>
  );
}

const euro = (w) => `${Math.round(Number(w) || 0).toLocaleString('de-DE')} €`;

/* Lecks-Report (Stufe 4): je Leck Anzahl, geschätzter Monatswert, bis zu zehn Einträge mit Link, Knopf „Aufgaben anlegen“.
   Der Gesamtwert ist eine Obergrenze („bis zu“), weil ein Lead in mehreren Lecks stehen kann. */
function LecksKarte({ karte, d }) {
  const [busy, setBusy] = useState('');
  const [erledigt, setErledigt] = useState({});
  const anlegen = async (l) => {
    const a = l.knopf?.aktion || {};
    if (a.typ !== 'api' || !String(a.pfad || '').startsWith('/')) return;
    setBusy(l.key);
    try {
      const r = await api.request({ url: a.pfad, method: a.methode || 'POST', data: a.daten });
      toast.success(r?.data?.meldung || 'Aufgaben angelegt');
      setErledigt((e) => ({ ...e, [l.key]: r?.data?.meldung || 'Aufgaben angelegt' }));
    } catch (e) { toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Nicht angelegt'); }
    setBusy('');
  };
  return (
    <div className="mt-2 rounded-lg border border-border bg-background p-3 space-y-3" data-testid="pia-karte" data-art="lecks">
      {karte.titel && <div className="text-sm font-semibold">{karte.titel}</div>}
      {d.gesamt && <div className="text-xs text-muted-foreground" data-testid="pia-lecks-gesamt">{d.gesamt}</div>}
      {d.lecks.map((l) => (
        <div key={l.key} className="space-y-1" data-testid={`pia-leck-${l.key}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="text-sm font-medium">{l.titel} <span className="text-muted-foreground">({l.anzahl})</span></div>
            {l.monatswert > 0 && <div className="text-sm text-muted-foreground shrink-0">ca. {euro(l.monatswert)} / Monat</div>}
          </div>
          {l.anzahl === 0 && <div className="text-xs text-muted-foreground">Nichts gefunden.</div>}
          {l.eintraege.map((z, i) => <Zeile key={i} z={z} />)}
          {l.weitere > 0 && <div className="text-xs text-muted-foreground px-1">… und {l.weitere} weitere</div>}
          {l.knopf && (erledigt[l.key]
            ? <div className="text-xs text-muted-foreground">{erledigt[l.key]}</div>
            : <Button size="sm" variant="outline" disabled={busy === l.key} onClick={() => anlegen(l)}>{l.knopf.text}</Button>)}
        </div>
      ))}
    </div>
  );
}

export default function PiaKarte({ karte, onAktion, nachrichtId }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState('');
  // Bestätigungskarte (Dauerauftrag): nach Speichern/Abbrechen keine Knöpfe mehr — der Stand steht an der Nachricht (`karte.erledigt`)
  const [fertig, setFertig] = useState(() => ({ gespeichert: 'Gespeichert', abgebrochen: 'Abgebrochen' }[karte?.erledigt] || ''));
  const [stand, setStand] = useState(null);     // Schreib-Aktion: {status, schritte{name:{status}}}
  const aktionId = karte?.daten?.aktion_id;
  useEffect(() => {
    if (!aktionId) return undefined;
    let aktiv = true;
    api.get(`/assistenz/aktionen/${aktionId}`).then((r) => { if (aktiv) setStand(r.data); }).catch(() => {});
    return () => { aktiv = false; };
  }, [aktionId]);
  if (!karte) return null;
  const d = karte.daten && typeof karte.daten === 'object' ? karte.daten : {};
  const { zeilen, abschnitte, ...rest0 } = d;
  const rest = Object.fromEntries(Object.entries(rest0).filter(([k]) => !VERSTECKT.includes(k)));
  const schrittStatus = (k) => {
    const s = k.aktion?.daten?.schritt;
    if (!stand || !s) return null;
    if (s === 'alles') return Object.values(stand.schritte || {}).every((x) => x.status === 'erledigt') ? 'erledigt' : null;
    return stand.schritte?.[s]?.status || null;
  };
  const beendet = stand && ['abgebrochen', 'ersetzt', 'erledigt'].includes(stand.status);
  const angefangen = stand && Object.values(stand.schritte || {}).some((x) => x.status !== 'offen');

  const ausfuehren = async (k, i) => {
    const a = k.aktion || {};
    if (a.typ === 'verwerfen') {
      if (karte.art === 'dauerauftrag' && nachrichtId) api.post(`/assistenz/thread/${nachrichtId}/karte-abbrechen`).catch(() => {});
      setFertig('Abgebrochen'); return;
    }
    if (a.typ === 'link' && String(a.ziel || '').startsWith('/')) { navigate(a.ziel); return; }
    if (a.typ === 'api' && String(a.pfad || '').startsWith('/')) {
      setBusy(String(i));
      try {
        const daten = karte.art === 'dauerauftrag' && nachrichtId ? { ...(a.daten || {}), nachricht_id: nachrichtId } : a.daten;
        const r = await api.request({ url: a.pfad, method: a.methode || 'POST', data: daten });
        if (aktionId) {
          if (a.pfad.endsWith('/abbrechen')) setStand((s) => ({ ...(s || {}), status: 'abgebrochen' }));
          else setStand({ status: r.data.status, schritte: r.data.schritte });
          if (r?.data?.fehler) toast.error(r.data.meldung); else toast.success(r?.data?.meldung || 'Erledigt');
        } else {
          toast.success(r?.data?.meldung || 'Erledigt');
          if (karte.art === 'dauerauftrag') setFertig('Gespeichert');
        }
        window.dispatchEvent(new Event('pia-auftraege-aktualisiert'));
        onAktion?.(r?.data);
      } catch (e) {
        const st = e?.response?.status;
        toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Nicht ausgeführt');
        if (aktionId && st === 409) api.get(`/assistenz/aktionen/${aktionId}`).then((r) => setStand(r.data)).catch(() => {});
        if (karte.art === 'dauerauftrag' && st === 409 && /schon erledigt/i.test(String(e?.response?.data?.detail || ''))) setFertig('Schon erledigt');
      }
      setBusy('');
    }
  };

  if (karte.art === 'lecks' && Array.isArray(d.lecks)) return <LecksKarte karte={karte} d={d} />;

  const knoepfe = (karte.knoepfe || []).filter((k) => !(beendet && k.aktion?.typ === 'api') && !(angefangen && String(k.aktion?.pfad || '').endsWith('/abbrechen')));
  return (
    <div className="mt-2 rounded-lg border border-border bg-background p-3 space-y-2" data-testid="pia-karte" data-art={karte.art}>
      {karte.titel && <div className="text-sm font-semibold">{karte.titel}</div>}
      {Array.isArray(abschnitte) && abschnitte.map((a, i) => (
        <div key={i}><div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{text(a.titel)}</div><div className="text-sm whitespace-pre-wrap">{text(a.text)}</div></div>
      ))}
      {Array.isArray(zeilen) && <div>{zeilen.map((z, i) => <Zeile key={i} z={z} />)}</div>}
      {Object.entries(rest).map(([k, v]) => <div key={k} className="text-sm"><span className="text-muted-foreground">{k}: </span>{text(v)}</div>)}
      {fertig && <div className="text-xs text-muted-foreground">{fertig}</div>}
      {stand?.status === 'abgebrochen' && <div className="text-xs text-muted-foreground">Abgebrochen – es wurde nichts geändert.</div>}
      {stand?.status === 'ersetzt' && <div className="text-xs text-muted-foreground">Ersetzt durch eine neuere Nachbereitung.</div>}
      {!fertig && knoepfe.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {knoepfe.map((k, i) => {
            if (k.aktion?.typ === 'sprachnotiz') return <SprachnotizBox key={i} pfad={k.aktion.pfad} onFertig={onAktion} />;
            const st = schrittStatus(k);
            const erledigt = st === 'erledigt';
            return (
              <Button key={i} size="sm" variant={i === 0 ? 'default' : 'outline'} disabled={busy === String(i) || erledigt || st === 'laeuft' || st === 'fehler'}
                onClick={() => ausfuehren(k, i)} data-testid={k.aktion?.daten?.schritt ? `pia-schritt-${k.aktion.daten.schritt}` : undefined}>
                {erledigt ? `✓ ${k.text}` : k.text}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
}
